import "prosemirror-view/style/prosemirror.css";
import "./styles.scss";

import * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { EditorState, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { produce } from "immer";
import { VirtualElement } from "@popperjs/core/lib/types";

import Toolbar, { ToolbarOptions, defaultToolbarOptions } from "./Toolbar";
import { useStableGetter } from "./Hooks";
import {
	createDecorations,
	InternalAnnotation,
	transactAnnotations,
	addHighlight as addHighlightToSet,
	removeHighlights as removeHighlightsFromSet,
	HighlighterColor,
	CommentPayload,
	CommentType,
	makeNewComment,
} from "./Annotations";
import { CommentViewer } from "./CommentViewer";

export interface Props {
	state: EditorState;
	changeState(newState: EditorState): void;
	/** Annotations. */
	annotations: InternalAnnotation[];
	changeAnnotations(newAnnotations: InternalAnnotation[]): void;
	/** If true, the text editor will be entirely readonly. */
	readonly?: boolean;
	/** If true, pasting and dropping in the editor will be impossible. */
	disablePasteDrop?: boolean;
	/**
	 * If set, an approximiate character limit above which new updates will be rejected.
	 * Will not make any attempt to truncate existing essays.
	 */
	characterLimit?: number;
	/**
	 * If set, when the user attempts to delete approximately this many characters at once or more, they'll get a confirmation message.
	 */
	deleteThreshold?: number;
	/** Dialog handler callbacks.  If not provided, characterLimit and deleteThreshold features will use the browser-provided fallbacks. */
	dialogActions?: {
		alert(title: string, message: string): Promise<void>;
		confirm(title: string, message: string): Promise<boolean>;
	};
	toolbarOptions?: ToolbarOptions;
	id?: string;
}

async function alertFallback(title: string, message: string) {
	alert(`${title} - ${message}`);
}
async function confirmFallback(title: string, message: string) {
	return confirm(`${title} - ${message}`);
}

export type HighlightingMode = "hidden" | "shown" | HighlighterColor | "remove";
/** Return the color when highlighting mode is set to add highlights, or null. */
function highlightingModeIsAdding(h: HighlightingMode) {
	switch (h) {
		case "hidden":
		case "shown":
		case "remove":
			return null;
		default:
			return h;
	}
}
export type CommentingMode = "hidden" | "shown" | CommentType;
function commentingModeIsAdding(c: CommentingMode) {
	switch (c) {
		case "hidden":
		case "shown":
			return null;
		default:
			return c;
	}
}

interface EditingComment {
	batch: number[];
	index: number;
	anchor: Element | VirtualElement;
	/** Force remounting of the comment box if we load another one while one is already open. */
	remountKey: string;
}

export default function RichTextEditor(props: Props) {
	const toolbarOptions = props.toolbarOptions || defaultToolbarOptions;

	const [highlightingMode, changeHighlightingMode] = useState<HighlightingMode>(
		toolbarOptions.highlightsShown ? "shown" : "hidden"
	);
	const [commentingMode, changeCommentingMode] = useState<CommentingMode>(
		toolbarOptions.commentsShown ? "shown" : "hidden"
	);

	const [editingComment, changeEditingComment] = useState<EditingComment | null>(null);
	const editingCommentRef = useRef<EditingComment | null>(null);
	useEffect(() => {
		editingCommentRef.current = editingComment;
	}, [editingComment]);

	const readonly = !!(props.readonly || editingComment);

	const contentDiv = useRef<HTMLDivElement | null>(null);

	const editorStateRef = useRef({ state: props.state, annotations: props.annotations });
	useEffect(() => {
		editorStateRef.current = { state: props.state, annotations: props.annotations };
	}, [props.state, props.annotations]);

	/** When we are confirming a large deletion, the DOM is still active and theoretically events could fire in the background. */
	const confirmationInProgress = useRef(false);

	const runTransactionInternal = useCallback(
		async (tr: Transaction, shouldFocus: boolean) => {
			if (confirmationInProgress.current) {
				return;
			}

			const newState = editorStateRef.current.state.apply(tr);
			const newSize = newState.doc.nodeSize;
			const oldSize = editorStateRef.current.state.doc.nodeSize;

			if (props.characterLimit != null) {
				// Only limit writing when we're making the document bigger.
				if (newSize > oldSize) {
					// doc.nodeSize always overestimates the character count.  At the same time, it's reasonably fast
					// to compute and does stop things like infinite `<p></p><p></p><p></p>` even when they contain no text.
					// To compensate, multiply by 1.05 so the user will be able to enter at least characterLimit characters most of the time.
					if (newSize > props.characterLimit * 1.05) {
						confirmationInProgress.current = true;
						try {
							const dialogAlert = props.dialogActions?.alert ?? alertFallback;
							await dialogAlert(
								"Limit Reached",
								"You have reached the character limit."
							);
							return;
						} finally {
							confirmationInProgress.current = false;
						}
					}
				}
			}

			if (props.deleteThreshold != null) {
				if (oldSize - newSize > props.deleteThreshold) {
					confirmationInProgress.current = true;
					try {
						const dialogConfirm = props.dialogActions?.confirm ?? confirmFallback;
						const confirmed = await dialogConfirm(
							"Confirm Deletion",
							"You just removed a large portion of text. Did you mean to do this?"
						);
						if (!confirmed) {
							return;
						}
					} finally {
						confirmationInProgress.current = false;
					}
				}
			}

			const newAnnotations = transactAnnotations(tr, editorStateRef.current.annotations);
			unstable_batchedUpdates(() => {
				props.changeState(newState);
				props.changeAnnotations(newAnnotations);
			});
			if (shouldFocus) {
				editor.current!.focus();
			}
		},
		[
			props.changeState,
			props.changeAnnotations,
			props.characterLimit,
			props.deleteThreshold,
			props.dialogActions,
		]
	);
	const dispatchTransaction = useCallback(
		(tr: Transaction) => runTransactionInternal(tr, false),
		[runTransactionInternal]
	);
	const dispatchTransactionForButton = useCallback(
		(tr: Transaction) => runTransactionInternal(tr, true),
		[runTransactionInternal]
	);

	const editor = useRef<EditorView | null>(null);
	if (props.id) {
		editor.current?.dom.setAttribute("id", props.id);
	}

	const decorations = useMemo(() => {
		let { annotations } = props;
		if (highlightingMode === "hidden") {
			annotations = annotations.filter((a) => a.payload.type !== "highlighter");
		}
		if (commentingMode === "hidden") {
			annotations = annotations.filter((a) => a.payload.type !== "comment");
		}
		return annotations.length ? createDecorations(props.state.doc, annotations) : null;
	}, [
		highlightingMode === "hidden",
		commentingMode === "hidden",
		props.state.doc,
		props.annotations,
	]);
	const getDecorations = useStableGetter(decorations);
	const getIsEditable = useStableGetter(!readonly);
	useLayoutEffect(() => {
		editor.current = new EditorView(contentDiv.current!, {
			state: props.state,
			dispatchTransaction,
			decorations: getDecorations,
			editable: getIsEditable,
		});
		return () => editor.current!.destroy();
	}, []);
	useLayoutEffect(() => {
		editor.current!.updateState(props.state);
	}, [props.state, decorations, readonly]);

	useEffect(() => {
		editor.current!.setProps({
			handleDrop: props.disablePasteDrop ? () => true : undefined,
			handlePaste: props.disablePasteDrop ? () => true : undefined,
		});
	}, [props.disablePasteDrop]);

	function createVirtualCommentAnchor(commentIds: string): VirtualElement {
		// This one is quite a pickle.  If you edit the text of a comment in the comment box (what you're normally going to do),
		// that triggers an annotations update which triggers a decorations update which triggers a decorations rerender which
		// removes the old decoration element from the screen entirely.  We need that to stick around so the comment box
		// can stay attached to it.
		// Since we haven't changed any aspect of the comment that impacts the annotation rendered for ProseMirror, we know
		// it will create another element with the same data-comment-ids as before.  So we can either (A) query for that dynamically,
		// or (B) replumb the annotation system to not regenerate decorations when only comment text changes.

		// Would (B) have been easier?  We may never know...
		const proseMirrorDiv = contentDiv.current!.querySelector(".ProseMirror")!;
		return {
			getBoundingClientRect() {
				return proseMirrorDiv
					.querySelector(`[data-comment-ids="${commentIds}"]`)!
					.getBoundingClientRect();
			},
			contextElement: proseMirrorDiv,
		};
	}

	/** The handler for highlighting/annotating things, with the two modes unbound so it can be called in other places. */
	const handleAnnotationMouseUp = useCallback(
		(highlightingMode: HighlightingMode, commentingMode: CommentingMode) => {
			const selection = document.getSelection();
			if (!(selection && selection.anchorNode && selection.focusNode)) {
				// Document unfocused?  Not sure, but can't do anything anyway.
				return false;
			}

			let from = editor.current!.posAtDOM(selection.anchorNode, selection.anchorOffset);
			let to = editor.current!.posAtDOM(selection.focusNode, selection.focusOffset);
			if (from < 0 || to < 0) {
				// Selection is not in this RTE
				return false;
			}
			if (from === to) {
				// Selection is just a single cursor location; let's not take any highlighting action
				return false;
			}
			if (from > to) {
				// Do nothing special with things that were selected backwards.
				[from, to] = [to, from];
			}

			let color: HighlighterColor | null;
			let commentType: CommentType | null;

			if (highlightingMode === "remove") {
				props.changeAnnotations(removeHighlightsFromSet(props.annotations, from, to));
			} else if ((color = highlightingModeIsAdding(highlightingMode))) {
				props.changeAnnotations(addHighlightToSet(props.annotations, from, to, color!));
			} else if ((commentType = commentingModeIsAdding(commentingMode))) {
				const comment = makeNewComment(from, to, commentType!);
				props.changeAnnotations([...props.annotations, comment]);
				changeCommentingMode("shown");

				// This is a bit ugly.  We want to load the editor on this comment, exactly as we'd load it on any other comment,
				// which means plucking it out of the DOM.
				// Technically we could surface it directly from the bowels of createDecoration().  That is also ugly.
				(async () => {
					await Promise.resolve();
					const editorDiv = contentDiv.current!;
					if (editorDiv) {
						const candidates =
							editorDiv.querySelectorAll<HTMLElement>("[data-comment-ids]");
						for (let i = 0; i < candidates.length; i++) {
							const commentIds = candidates[i].dataset.commentIds!;
							const ids = commentIds.split(",").map(Number);
							const index = ids.indexOf(comment.payload.id);
							if (index >= 0) {
								changeEditingComment({
									batch: ids,
									index,
									anchor: createVirtualCommentAnchor(commentIds),
									remountKey: commentIds,
								});
								await Promise.resolve();
								// Somehow, we have to clear the selection again here.
								// Mucking with the DOM in the end of the microtask probably beefed it.
								document.getSelection()?.collapseToStart();
								break;
							}
						}
					}
				})();
			} else {
				return false;
			}

			selection.collapseToStart(); // Now, unselect so the user can see that their highlights have applied.
			return true;
		},
		[props.changeAnnotations, props.annotations]
	);

	useEffect(() => {
		function handleMouseUp() {
			handleAnnotationMouseUp(highlightingMode, commentingMode);
		}
		document.addEventListener("mouseup", handleMouseUp, { passive: true, capture: true });
		return () => document.removeEventListener("mouseup", handleMouseUp, { capture: true });
	}, [highlightingMode, commentingMode, handleAnnotationMouseUp]);

	function handleCommentClick(e: React.MouseEvent) {
		let element = e.target as HTMLElement;
		if (element.closest(".annotation-comment-clicker")) {
			element = element.closest(".annotation-comment-clicker")!;
		}
		if (element.classList.contains("annotation-comment-clicker")) {
			if (element.classList.contains("annotations-comment-active")) {
				clearActiveComments();
				closeCommentViewer();
			} else {
				// Feels like I'm in the jquery days again!  But this is a very special case, really.
				setActiveComment(element);
				const commentIds = element.dataset.commentIds!;
				const comments = commentIds.split(",").map(Number);
				changeEditingComment({
					batch: comments,
					index: 0,
					anchor: createVirtualCommentAnchor(commentIds),
					remountKey: commentIds,
				});
			}
		}
	}

	function clearActiveComments() {
		Array.from(document.querySelectorAll(".annotations-comment-active")).forEach((el) =>
			el.classList.remove("annotations-comment-active")
		);
	}

	function setActiveComment(element: HTMLElement) {
		clearActiveComments();
		element.classList.add("annotations-comment-active");
	}

	function isElementInViewport(element: Element) {
		var rect = element.getBoundingClientRect();

		return (
			rect.top >= 0 &&
			rect.left >= 0 &&
			rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
			rect.right <= (window.innerWidth || document.documentElement.clientWidth)
		);
	}

	function isElementInView(container: Element, element: Element) {
		var crect = container.getBoundingClientRect();
		var erect = element.getBoundingClientRect();

		return (
			erect.top >= crect.top &&
			erect.left >= crect.left &&
			erect.bottom <= crect.bottom &&
			erect.right <= crect.right
		);
	}

	const gotoPrevComment = useCallback(() => {
		const div = contentDiv.current;
		const editingComment = editingCommentRef.current;
		if (div && editingComment) {
			const id = editingComment.batch[editingComment.index];
			const candidates = div.querySelectorAll<HTMLElement>("[data-comment-ids]");
			for (let i = 0; i < candidates.length; i++) {
				const commentIds = candidates[i].dataset.commentIds!;
				const ids = commentIds.split(",").map(Number);
				const index = ids.indexOf(id);
				if (index >= 0) {
					if (i === 0 && index === 0) {
						// first comment, cannot go to previous
						break;
					} else if (index > 0) {
						// move to previous within the group
						return changeEditingComment(
							produce((c) => {
								c!.index--;
							})
						);
					} else {
						// move to last comment of previous group
						const target = candidates[i - 1];
						const commentIds = target.dataset.commentIds!;
						const ids = commentIds.split(",").map(Number);
						const pmDiv = div.querySelector(".ProseMirror")!;

						if (!isElementInView(pmDiv, target)) {
							const lineHeight = parseFloat(
								window.getComputedStyle(target).getPropertyValue("line-height")
							);
							const pos = target.offsetTop - lineHeight;
							pmDiv.scrollTo({ top: pos });
						}

						if (!isElementInViewport(target)) {
							window.scrollTo({ top: target.offsetTop });
						}

						setActiveComment(target);

						return changeEditingComment({
							batch: ids,
							index: ids.length - 1,
							anchor: createVirtualCommentAnchor(commentIds),
							remountKey: commentIds,
						});
					}
				}
			}
		}
	}, []);

	const gotoNextComment = useCallback(() => {
		const div = contentDiv.current;
		const editingComment = editingCommentRef.current;
		if (div && editingComment) {
			const id = editingComment.batch[editingComment.index];
			const candidates = div.querySelectorAll<HTMLElement>("[data-comment-ids]");
			for (let i = 0; i < candidates.length; i++) {
				const commentIds = candidates[i].dataset.commentIds!;
				const ids = commentIds.split(",").map(Number);
				const index = ids.indexOf(id);
				if (index >= 0) {
					if (i === candidates.length - 1 && index === ids.length - 1) {
						// last comment, cannot go to next
						break;
					} else if (index < ids.length - 1) {
						// move to next within the group
						return changeEditingComment(
							produce((c) => {
								c!.index++;
							})
						);
					} else {
						// move to first comment of next group
						const target = candidates[i + 1];
						const commentIds = target.dataset.commentIds!;
						const ids = commentIds.split(",").map(Number);
						const pmDiv = div.querySelector(".ProseMirror")!;

						if (!isElementInView(pmDiv, target)) {
							const lineHeight = parseFloat(
								window.getComputedStyle(target).getPropertyValue("line-height")
							);
							const pos = target.offsetTop - lineHeight;
							pmDiv.scrollTo({ top: pos });
						}

						if (!isElementInViewport(target)) {
							window.scrollTo({ top: target.offsetTop });
						}

						setActiveComment(target);

						return changeEditingComment({
							batch: ids,
							index: 0,
							anchor: createVirtualCommentAnchor(commentIds),
							remountKey: commentIds,
						});
					}
				}
			}
		}
	}, []);

	const closeCommentViewer = useCallback(() => changeEditingComment(null), []);

	function renderCommentViewer() {
		if (!editingComment) {
			return null;
		}
		const id = editingComment.batch[editingComment.index];
		const annotationIndex = props.annotations.findIndex(
			(a) => a.payload.type === "comment" && a.payload.id === id
		);
		const { payload } = props.annotations[annotationIndex] as { payload: CommentPayload };

		const comments = contentDiv.current!.querySelectorAll<HTMLElement>("[data-comment-ids]");

		const firstCommentId = ((comments: NodeListOf<HTMLElement>): Number | null => {
			if (!comments) return null;
			const commentIds = comments[0].dataset.commentIds!;
			const ids = commentIds.split(",").map(Number);
			return ids[0];
		})(comments);

		const lastCommentId = ((comments: NodeListOf<HTMLElement>): Number | null => {
			if (!comments) return null;
			const commentIds = comments[comments.length - 1].dataset.commentIds!;
			const ids = commentIds.split(",").map(Number);
			return ids[ids.length - 1];
		})(comments);

		function handleChangeText(s: string) {
			props.changeAnnotations(
				produce(props.annotations, (draft) => {
					(draft[annotationIndex].payload as CommentPayload).text = s;
				})
			);
		}

		function handleDelete() {
			unstable_batchedUpdates(() => {
				props.changeAnnotations(
					produce(props.annotations, (draft) => {
						draft.splice(annotationIndex, 1);
					})
				);
				changeEditingComment(null);
			});
		}

		return (
			<CommentViewer
				key={editingComment.remountKey}
				id={id}
				type={payload.commentType}
				anchor={editingComment.anchor}
				text={payload.text}
				close={closeCommentViewer}
				prev={id !== firstCommentId ? gotoPrevComment : undefined}
				next={id !== lastCommentId ? gotoNextComment : undefined}
				changeText={payload.editable ? handleChangeText : undefined}
				delete={payload.editable ? handleDelete : undefined}
			/>
		);
	}

	const changeCommentingModeFromToolbar = useCallback(
		(mode: CommentingMode) => {
			if (mode === "hidden") {
				changeCommentingMode(mode);
				changeEditingComment(null);
			} else if (!handleAnnotationMouseUp("hidden", mode)) {
				// Only activate the mode, if we couldn't immediately add a comment.
				changeCommentingMode(mode);
			}
		},
		[handleAnnotationMouseUp]
	);

	const contentClasses = ["content", `highlighting-${highlightingMode}`];
	if (highlightingModeIsAdding(highlightingMode)) {
		contentClasses.push("highlighting-add");
	}

	const cafeRteClasses = ["cafe-rich-text-editor"];
	if (toolbarOptions.position == "top") {
		cafeRteClasses.push("toolbar-on-top");
	}

	return (
		<div className={cafeRteClasses.join(" ")} onClickCapture={handleCommentClick}>
			<div ref={contentDiv} className={contentClasses.join(" ")} />
			<Toolbar
				editorState={props.state}
				dispatch={dispatchTransactionForButton}
				highlightingMode={highlightingMode}
				changeHighlightingMode={changeHighlightingMode}
				commentingMode={commentingMode}
				changeCommentingMode={changeCommentingModeFromToolbar}
				options={toolbarOptions}
				readonly={readonly}
			/>
			{renderCommentViewer()}
		</div>
	);
}
